import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApiService } from '../../services/adminApi';
import { usePageClamp } from '../../hooks/usePageClamp';
import { useSearchDraft } from '../../hooks/useSearchDraft';
import { urlEnum, urlOptionalId, urlPositiveInt, urlString, useUrlState } from '../../hooks/useUrlState';
import { SectionFilterBar, TableCard, TableHeader, TableViewport, usePageSize } from '../table/TableKit';
import {
  Badge,
  EmptyRow,
  ErrorNotice,
  InstanceSelect,
  LoadingSpinner,
  SortableTh,
  formatDuration,
  formatNumber,
  instanceLabel,
  selectClass,
  tdClass,
  thClass,
} from './shared';
import RelativeTime from '../RelativeTime';

const URL_SPEC = {
  instance: urlOptionalId(),
  status: urlEnum(['', 'pending', 'running', 'completed', 'failed', 'cancelled'] as const, ''),
  operation: urlEnum(['', 'analyze', 'convert'] as const, ''),
  q: urlString(),
  sort: urlEnum(['instance_name', 'status', 'operation', 'project_name', 'license_tokens', 'created_at', 'duration'] as const, 'created_at'),
  order: urlEnum(['asc', 'desc'] as const, 'desc'),
  page: urlPositiveInt(),
};

function AdminJobs() {
  const [pageSize, setPageSize] = usePageSize('admin-jobs');
  const [state, setState] = useUrlState(URL_SPEC);
  const [searchInput, setSearchInput] = useSearchDraft(state.q, (q) => setState({ q, page: 1 }));
  const offset = (state.page - 1) * pageSize;

  const params = useMemo(
    () => ({
      limit: pageSize,
      offset,
      instance_id: state.instance,
      status: state.status || undefined,
      operation: state.operation || undefined,
      search: state.q || undefined,
      sort: state.sort,
      order: state.order,
    }),
    [pageSize, offset, state.instance, state.status, state.operation, state.q, state.sort, state.order],
  );

  const { data, isLoading, isPlaceholderData, error } = useQuery({
    queryKey: ['admin', 'jobs', params],
    queryFn: () => adminApiService.getJobs(params),
    placeholderData: (prev) => prev,
    retry: false,
  });

  usePageClamp({
    page: state.page,
    pageSize,
    total: data?.total,
    ready: !isLoading && !isPlaceholderData && !!data,
    setPage: (page) => setState({ page }),
  });

  const onSort = (sort: string, order: 'asc' | 'desc') => setState({
    sort: sort as typeof state.sort,
    order,
    page: 1,
  });

  return (
    <div>
      {error && <ErrorNotice message="Failed to load jobs." />}
      <SectionFilterBar>
        <InstanceSelect
          value={state.instance}
          onChange={(instance) => setState({ instance, page: 1 })}
        />
        <select
          aria-label="Status filter"
          className={selectClass}
          value={state.status}
          onChange={(e) => setState({ status: e.target.value as typeof state.status, page: 1 })}
        >
          <option value="">All Statuses</option>
          {['pending', 'running', 'completed', 'failed', 'cancelled'].map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <select
          aria-label="Operation filter"
          className={selectClass}
          value={state.operation}
          onChange={(e) => setState({ operation: e.target.value as typeof state.operation, page: 1 })}
        >
          <option value="">All Operations</option>
          <option value="analyze">analyze</option>
          <option value="convert">convert</option>
        </select>
      </SectionFilterBar>
      <TableCard>
        <TableHeader
          title="Jobs"
          search={{
            value: searchInput,
            onChange: setSearchInput,
            ariaLabel: 'Search jobs',
          }}
          pagination={{
            offset,
            limit: pageSize,
            total: data?.total ?? 0,
            onOffsetChange: (nextOffset) => setState({ page: nextOffset / pageSize + 1 }),
            onLimitChange: (size) => {
              setPageSize(size);
              setState({ page: 1 });
            },
          }}
        />
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <TableViewport>
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className={thClass}>Job</th>
                  <SortableTh label="Instance" sortKey="instance_name" sort={state.sort} order={state.order} onSort={onSort} firstOrder="asc" />
                  <SortableTh label="Status" sortKey="status" sort={state.sort} order={state.order} onSort={onSort} firstOrder="asc" />
                  <SortableTh label="Operation" sortKey="operation" sort={state.sort} order={state.order} onSort={onSort} firstOrder="asc" />
                  <SortableTh label="Project" sortKey="project_name" sort={state.sort} order={state.order} onSort={onSort} firstOrder="asc" />
                  <th className={thClass}>Progress</th>
                  <SortableTh label="Tokens" sortKey="license_tokens" sort={state.sort} order={state.order} onSort={onSort} />
                  <SortableTh label="Created" sortKey="created_at" sort={state.sort} order={state.order} onSort={onSort} />
                  <SortableTh label="Duration" sortKey="duration" sort={state.sort} order={state.order} onSort={onSort} />
                  <th className={thClass}>Error</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {(data?.items ?? []).map((job) => (
                  <tr key={job.id}>
                    <td className={`${tdClass} font-mono`} title={job.id}>{job.id.slice(0, 8)}…</td>
                    <td className={tdClass}>{instanceLabel(job.instance_name, job.instance_key)}</td>
                    <td className={tdClass}><Badge value={job.status} /></td>
                    <td className={tdClass}>{job.operation}</td>
                    <td className={tdClass}>{job.project_name || '—'}</td>
                    <td className={tdClass}>{job.status === 'running' ? `${job.progress}%` : '—'}</td>
                    <td className={tdClass}>{formatNumber(job.license_tokens)}</td>
                    <td className={tdClass}><RelativeTime value={job.created_at} /></td>
                    <td className={tdClass}>{formatDuration(job.duration_seconds)}</td>
                    <td className={`${tdClass} max-w-xs truncate`} title={job.error_message ?? ''}>
                      {job.error_message || '—'}
                    </td>
                  </tr>
                ))}
                {(data?.items ?? []).length === 0 && <EmptyRow colSpan={10} text="No jobs found." />}
              </tbody>
            </table>
          </TableViewport>
        )}
      </TableCard>
    </div>
  );
}

export default AdminJobs;
